import { Button } from '#app/components/Buttons/Button.js';
import { Menu } from '#app/components/Menu/Menu.js';
import { useAnalytics } from '#app/hooks/useAnalytics.js';
import { useCopyToClipboard } from '#app/hooks/useCopyToClipboard.js';
import { type EventLibrary } from '#app/types/analytics/eventLibrary.js';
import { CopyIcon, ShareAltIcon, UploadIcon } from '@contra/icons/icons';
import { useCallback, useState } from 'react';
import { css, styled, useTheme } from 'styled-components';

type ShareButtonProps = {
  readonly analyticsProperties: Omit<
    EventLibrary['menu'],
    'action' | 'menu_name'
  >;
  readonly toastSuccessMessage: string;
  readonly url: string;
  readonly variant?: 'androidShare' | 'minimal' | 'shareLink' | 'upload';
};

const UploadButton = styled(Button)`
  ${({ theme }) => css`
    pointer-events: all;
    border-radius: 50%;
    padding: 0;
    border: 0;
    height: 48px;
    width: 48px;
    color: ${theme.colorsV2.white};

    * {
      line-height: 1;
    }

    svg {
      display: block;
    }
  `}
`;

const AndroidShareButton = styled.button`
  ${({ theme }) => css`
    display: flex;
    align-items: center;
    justify-content: center;
    pointer-events: all;
    border-radius: 50%;
    padding: 0;
    border: 0;
    height: 48px;
    width: 48px;
    color: ${theme.colorsV2.white};
    background-color: ${theme.colorsV2.gray05};

    svg {
      display: block;
      height: 16px;
      width: 16px;
    }

    ${theme.mediaQueries.md} {
      height: 64px;
      width: 64px;

      svg {
        height: 24px;
        width: 24px;
      }
    }
  `}
`;

const MenuPanel = styled(Menu.Panel)`
  width: 256px;
  pointer-events: all;
`;

const CopyContainer = styled(Menu.Item)`
  padding: 4px;

  & > span {
    display: flex;
    flex-direction: row;
    align-items: center;
    width: 100%;
    margin: 0;
  }
`;

const CopyBoxContainer = styled.div`
  ${({ theme }) => css`
    width: 100%;
    border-top-left-radius: 8px;
    border-top-right-radius: 8px;
    border: 1px solid ${theme.colorsV2.gray30};
    height: 40px;
    padding: 8px 12px;

    ${theme.mediaQueries.lg} {
      width: 192px;
    }
  `}
`;

const CopyBox = styled.div`
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow: hidden;
`;

const StyledCopyIcon = styled(CopyIcon)`
  margin-left: 16px;
  > path {
    stroke-width: 1px;
  }
`;

export const ShareButton = ({
  analyticsProperties,
  toastSuccessMessage,
  url,
  variant = 'upload',
}: ShareButtonProps) => {
  const [isMenuOpen, setMenuOpen] = useState(false);
  const theme = useTheme();
  const copyToClipboard = useCopyToClipboard();

  const { track } = useAnalytics();

  const toggleMenu = useCallback(() => {
    if (!isMenuOpen)
      track('menu', {
        action: 'tapped',
        menu_name: 'share',
        ...analyticsProperties,
      });
    setMenuOpen(!isMenuOpen);
  }, [analyticsProperties, isMenuOpen, setMenuOpen, track]);

  const copyUrl = useCallback(async () => {
    track('button', {
      action: 'tapped',
      button_name: 'copy_url',
      ...analyticsProperties,
    });
    await copyToClipboard(url, {
      successToast: toastSuccessMessage,
    });
  }, [analyticsProperties, copyToClipboard, toastSuccessMessage, track, url]);

  return (
    <Menu
      aria-label="Open share menu"
      isOpen={isMenuOpen}
      offset={[0, null]}
      placement="bottom-end"
      toggleMenu={toggleMenu}
    >
      <Menu.Reference>
        {variant === 'upload' ? (
          <UploadButton
            aria-label="Open share menu"
            onClick={toggleMenu}
            removeMinWidth
            type="button"
            variant="V2TertiaryOutline"
          >
            <UploadIcon
              color={theme.colorsV2.black}
              size={24}
            />
          </UploadButton>
        ) : null}

        {variant === 'androidShare' ? (
          <AndroidShareButton
            aria-label="Open share menu"
            onClick={toggleMenu}
            type="button"
          >
            <ShareAltIcon
              color={theme.colorsV2.gray70}
              size={16}
            />
          </AndroidShareButton>
        ) : null}

        {variant === 'shareLink' ? (
          <Button
            aria-label="Open share menu"
            icon={ShareAltIcon}
            onClick={toggleMenu}
            variant="V2Link"
          >
            Share
          </Button>
        ) : null}

        {variant === 'minimal' ? (
          <ShareAltIcon
            aria-label="Open share menu"
            onClick={toggleMenu}
            role="button"
            size={16}
          />
        ) : null}
      </Menu.Reference>
      <MenuPanel>
        <CopyContainer onClick={copyUrl}>
          <CopyBoxContainer>
            <CopyBox aria-label="Copy link">{url}</CopyBox>
          </CopyBoxContainer>
          <StyledCopyIcon size={24} />
        </CopyContainer>
      </MenuPanel>
    </Menu>
  );
};
